import { copyToClipboard } from 'common/commonFunctions';
import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useGetDepartmentsQuery } from 'redux/features/departmentApi';
import { useGetUserByIdQuery, useUpdateEmployeeMutation } from 'redux/features/users/userApi';

import { useGetDesignationsByDepartmentQuery } from 'redux/features/designationApi';
import ThreeDotLoading from 'components/ui/ThreeDotLoading';
import ConfirmationModal from 'components/ui/ConfirmationModal';
import Breadcrumb from 'components/ui/Breadcrumb';
import Icon from '../../components/AppIcon';
import CommandPalette from '../../components/ui/CommandPalette';
import Header from '../../components/ui/Header';
import Sidebar from '../../components/ui/Sidebar';

import BankDetailsTab from './BanckDetailsTab';
import BondDetailsTab from './BondDetailsTab';
import Permissions from './components/Permissions';
import FamilyDetails from './components/FamilyDetails';
import EmployeePeriods from './components/EmployeePeriods';

const EmployeeEditFullPage = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const employeeId = searchParams?.get('id');
  const [departments, setDepartments] = useState([]);
  const [employee, setEmployee] = useState(null);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [updateEmployee] = useUpdateEmployeeMutation();
  const [selectedDepartmentId, setSelectedDepartmentId] = useState();
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'personal');
  const handleTabChange = (tabName) => {
    setActiveTab(tabName);
    const currentParams = Object.fromEntries([...searchParams]);
    // Replace instead of push so tab switches don't pile up browser history entries
    setSearchParams({ ...currentParams, tab: tabName }, { replace: true });
  };

  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [modalConfig, setModalConfig] = useState({
    title: '',
    message: '',
    onConfirm: () => {},
  });
  const { data: departmentsData } = useGetDepartmentsQuery();
  const id = searchParams?.get('id');
  // RTK Queries
  const {
    data: employeeData,
    isLoading,
    refetch,
  } = useGetUserByIdQuery(parseInt(id), { refetchOnMountOrArgChange: true });
  const { data: designationsData } = useGetDesignationsByDepartmentQuery(selectedDepartmentId, {
    skip: !selectedDepartmentId,
  });

  useEffect(() => {
    if (employeeData?.data) {
      setEmployee(employeeData?.data);
    }
    if (location?.hash) {
      const id = location?.hash?.replace('#', '');
      document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [employeeData, location?.hash]);

  useEffect(() => {
    const tab = searchParams.get('tab');
    if (tab && tab !== activeTab) {
      setActiveTab(tab);
    }
  }, [searchParams, activeTab]);

  const [formData, setFormData] = useState({
    // Personal Information
    first_name: '',
    middle_name: '',
    last_name: '',
    email: '',
    mobile: '',
    qualification: '',
    date_of_birth: '',
    gender: '',
    current_address: '',
    permanent_address: '',
    emergency_contact_number: '',
    college_name: '',
    marital_status: '',
    city: '',

    // Work Information
    department_id: null,
    designation_id: null,
    role: 'Employee',
    joining_date: '',
    current_experience_in_months: 0,
    previous_experience_in_months: 0,
    // Salary Information
    salary: {
      annual_salary: '',
      monthly_salary: '',
      house_rent_allowance: '',
      special_allowance: '',
      other_allowance: '',
      overtime_enabled: false,
      basic_salary: '',
    },
    paid_leave: 0,
    monthlyIncentives: [],
    override_settings: false,
  });

  const [errors, setErrors] = useState({});

  // Add new incentive
  const addIncentive = () => {
    setFormData((prev) => {
      const updatedIncentives = [...prev.monthlyIncentives, { title: '', amount: '' }];

      // Recalculate salary with new incentive
      const basic = parseFloat(prev.salary.basic_salary) || 0;
      const hra = parseFloat(prev.salary.house_rent_allowance) || 0;
      const sa = parseFloat(prev.salary.special_allowance) || 0;
      const oa = parseFloat(prev.salary.other_allowance) || 0;
      const incentivesTotal = updatedIncentives.reduce(
        (sum, item) => sum + (parseFloat(item.amount) || 0),
        0,
      );

      const totalMonthly = basic + hra + sa + oa + incentivesTotal;
      const updatedSalary = {
        ...prev.salary,
        monthly_salary: totalMonthly > 0 ? totalMonthly.toFixed(2) : '',
        annual_salary: totalMonthly > 0 ? (totalMonthly * 12).toFixed(2) : '',
      };

      return {
        ...prev,
        salary: updatedSalary,
        monthlyIncentives: updatedIncentives,
      };
    });
    setHasChanges(true);
  };

  // Remove incentive
  const removeIncentive = (index) => {
    setFormData((prev) => {
      const updatedIncentives = prev.monthlyIncentives.filter((_, i) => i !== index);

      // Recalculate salary without removed incentive
      const basic = parseFloat(prev.salary.basic_salary) || 0;
      const hra = parseFloat(prev.salary.house_rent_allowance) || 0;
      const sa = parseFloat(prev.salary.special_allowance) || 0;
      const oa = parseFloat(prev.salary.other_allowance) || 0;
      const incentivesTotal = updatedIncentives.reduce(
        (sum, item) => sum + (parseFloat(item.amount) || 0),
        0,
      );

      const totalMonthly = basic + hra + sa + oa + incentivesTotal;
      const updatedSalary = {
        ...prev.salary,
        monthly_salary: totalMonthly > 0 ? totalMonthly.toFixed(2) : '',
        annual_salary: totalMonthly > 0 ? (totalMonthly * 12).toFixed(2) : '',
      };

      return {
        ...prev,
        salary: updatedSalary,
        monthlyIncentives: updatedIncentives,
      };
    });
    setHasChanges(true);
  };

  // Update incentive
  const updateIncentive = (index, field, value) => {
    setFormData((prev) => {
      const updatedIncentives = prev.monthlyIncentives.map((incentive, i) =>
        i === index ? { ...incentive, [field]: value } : incentive,
      );

      // Recalculate salary with updated incentive
      const basic = parseFloat(prev.salary.basic_salary) || 0;
      const hra = parseFloat(prev.salary.house_rent_allowance) || 0;
      const sa = parseFloat(prev.salary.special_allowance) || 0;
      const oa = parseFloat(prev.salary.other_allowance) || 0;
      const incentivesTotal = updatedIncentives.reduce(
        (sum, item) => sum + (parseFloat(item.amount) || 0),
        0,
      );

      const totalMonthly = basic + hra + sa + oa + incentivesTotal;
      const updatedSalary = {
        ...prev.salary,
        monthly_salary: totalMonthly > 0 ? totalMonthly.toFixed(2) : '',
        annual_salary: totalMonthly > 0 ? (totalMonthly * 12).toFixed(2) : '',
      };

      return {
        ...prev,
        salary: updatedSalary,
        monthlyIncentives: updatedIncentives,
      };
    });
    setHasChanges(true);
  };

  // Load employee data and departments/designations
  useEffect(() => {
    const loadData = () => {
      setFormData({
        first_name: employee?.first_name || '',
        middle_name: employee?.middle_name || '',
        last_name: employee?.last_name || '',
        email: employee?.email || '',
        mobile: employee?.mobile || '',
        qualification: employee?.qualification || '',
        date_of_birth: employee?.date_of_birth ? employee.date_of_birth.split('T')[0] : '',
        gender: employee?.gender || '',
        current_address: employee?.current_address || '',
        permanent_address: employee?.permanent_address || '',
        emergency_contact_number: employee?.emergency_contact_number || '',
        college_name: employee?.college_name || '',
        marital_status: employee?.marital_status || '',
        city: employee?.city || '',
        department_id: employee?.department_id || null,
        designation_id: employee?.designation_id || null,
        role: employee?.role || 'EMPLOYEE',
        override_settings: employee?.override_settings || false,
        paid_leave: employee?.paid_leave || 0,
        current_experience_in_months: employee?.current_experience_in_months || 0,
        previous_experience_in_months: employee?.previous_experience_in_months || 0,
        joining_date: employee?.joining_date ? employee.joining_date.split('T')[0] : '',
        salary: {
          annual_salary: employee?.salary?.annual_salary || '',
          monthly_salary: employee?.salary?.monthly_salary || '',
          basic_salary: employee?.salary?.basic_salary || '',
          house_rent_allowance: employee?.salary?.house_rent_allowance || '',
          special_allowance: employee?.salary?.special_allowance || '',
          other_allowance: employee?.salary?.other_allowance || '',
          overtime_enabled: employee?.salary?.overtime_enabled || false,
        },
        monthlyIncentives: (employee?.monthlyIncentives || []).map((mi) => ({
          title: mi?.title || '',
          amount: mi?.amount != null ? String(mi.amount) : '',
          id: mi?.id || null,
        })),
      });

      // Load departments and designations
      if (departmentsData?.departments) {
        setDepartments(departmentsData.departments);
      }
      if (employee?.employee_id) {
        setSelectedDepartmentId(employee?.department_id);
      }
    };

    loadData();
  }, [employeeId, navigate, employee, departmentsData]);

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e?.target || {};

    setFormData((prev) => {
      // Check if the field belongs to the salary object
      const salaryFields = [
        'annual_salary',
        'monthly_salary',
        'house_rent_allowance',
        'special_allowance',
        'other_allowance',
        'overtime_enabled',
        'basic_salary',
      ];

      if (salaryFields.includes(name)) {
        const val = type === 'checkbox' ? checked : value;
        const updatedSalary = {
          ...prev.salary,
          [name]: val,
        };

        // Auto-calculate monthly and annual salary if components change
        if (
          ['basic_salary', 'house_rent_allowance', 'special_allowance', 'other_allowance'].includes(
            name,
          )
        ) {
          const basic = parseFloat(name === 'basic_salary' ? val : updatedSalary.basic_salary) || 0;
          const hra =
            parseFloat(
              name === 'house_rent_allowance' ? val : updatedSalary.house_rent_allowance,
            ) || 0;
          const sa =
            parseFloat(name === 'special_allowance' ? val : updatedSalary.special_allowance) || 0;
          const oa =
            parseFloat(name === 'other_allowance' ? val : updatedSalary.other_allowance) || 0;

          const incentivesTotal = prev.monthlyIncentives.reduce(
            (sum, item) => sum + (parseFloat(item.amount) || 0),
            0,
          );

          const totalMonthly = basic + hra + sa + oa + incentivesTotal;
          updatedSalary.monthly_salary = totalMonthly > 0 ? totalMonthly.toFixed(2) : '';
          updatedSalary.annual_salary = totalMonthly > 0 ? (totalMonthly * 12).toFixed(2) : '';
        }

        return {
          ...prev,
          salary: updatedSalary,
        };
      }

      const updated = {
        ...prev,
        [name]: type === 'checkbox' ? checked : value,
      };

      // If department changes, reset designation and filter designations
      if (name === 'department_id') {
        updated.department_id = parseInt(value);
        updated.designation_id = null;
        setSelectedDepartmentId(parseInt(value));
      }

      return updated;
    });

    setHasChanges(true);

    // Clear error for this field
    if (errors?.[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: '',
      }));
    }
  };

  // Validate form by tab
  const validateForm = (tab = null) => {
    const newErrors = {};

    // Personal Information validation
    if (!tab || tab === 'personal') {
      if (!formData?.first_name?.trim()) {
        newErrors.first_name = 'First Name is required';
      }
      if (!formData?.middle_name?.trim()) {
        newErrors.middle_name = 'Middle Name is required';
      }
      if (!formData?.last_name?.trim()) {
        newErrors.last_name = 'Last Name is required';
      }

      if (!formData?.email?.trim()) {
        newErrors.email = 'Email is required';
      } else if (!formData?.email?.includes('@')) {
        newErrors.email = 'Please enter a valid email address';
      }

      if (!formData?.mobile?.trim()) {
        newErrors.mobile = 'Mobile Number is required';
      } else if (!/^\d{10}$/?.test(formData?.mobile)) {
        newErrors.mobile = 'Please enter a valid 10-digit mobile number';
      }

      if (!formData?.date_of_birth) {
        newErrors.date_of_birth = 'Date of Birth is required';
      }

      if (!formData?.gender) {
        newErrors.gender = 'Gender is required';
      }

      if (!formData?.qualification?.trim()) {
        newErrors.qualification = 'Qualification is required';
      }

      if (!formData?.marital_status) {
        newErrors.marital_status = 'Martial Status is required';
      }
    }

    // Contact Information validation
    if (!tab || tab === 'contact' || !tab) {
      if (!formData?.city?.trim()) {
        newErrors.city = 'City is required';
      }
      if (!formData?.current_address?.trim()) {
        newErrors.current_address = 'Current Address is required';
      }
      if (!formData?.permanent_address?.trim()) {
        newErrors.permanent_address = 'Permanent Address is required';
      }
    }

    // Work Information validation
    if (!tab || tab === 'work') {
      if (!formData?.department_id) {
        newErrors.department_id = 'Department is required';
      }
      if (!formData?.designation_id) {
        newErrors.designation_id = 'Designation is required';
      }
      if (!formData?.role) {
        newErrors.role = 'Role is required';
      }
      if (!formData?.joining_date) {
        newErrors.joining_date = 'Joining Date is required';
      }
      if (!formData?.college_name?.trim()) {
        newErrors.college_name = 'College Name is required';
      }

      // Department-Designation relationship validation
      if (formData?.designation_id && !formData?.department_id) {
        newErrors.department_id = 'Please select a department first';
      }
    }

    // Salary validation
    if (!tab || tab === 'salary') {
      if (
        formData?.salary?.annual_salary &&
        (isNaN(formData?.salary?.annual_salary) || parseFloat(formData?.salary?.annual_salary) <= 0)
      ) {
        newErrors.annual_salary = 'Please enter a valid positive annual salary amount (integer)';
      }

      if (
        formData?.salary?.monthly_salary &&
        (isNaN(formData?.salary?.monthly_salary) ||
          parseFloat(formData?.salary?.monthly_salary) <= 0)
      ) {
        newErrors.monthly_salary = 'Please enter a valid positive monthly salary amount';
      }

      // Validate allowances and basic salary
      ['basic_salary', 'house_rent_allowance', 'special_allowance', 'other_allowance'].forEach(
        (field) => {
          if (
            formData?.salary?.[field] &&
            (isNaN(formData?.salary?.[field]) || parseFloat(formData?.salary?.[field]) < 0)
          ) {
            const label = field.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase());
            newErrors[field] = `Please enter a valid amount for ${label}`;
          }
        },
      );
    }

    // Salary Information validation
    if (!tab || tab === 'salary-info') {
      // Validate monthly incentives
      formData?.monthlyIncentives?.forEach((incentive, index) => {
        if (!incentive?.title?.trim()) {
          newErrors[`monthlyIncentives_${index}_title`] = 'Title is required';
        }
        if (
          incentive?.amount === '' ||
          incentive?.amount == null ||
          isNaN(incentive?.amount) ||
          parseFloat(incentive?.amount) <= 0
        ) {
          newErrors[`monthlyIncentives_${index}_amount`] = 'Amount must be greater than 0';
        }
      });
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  // Handle form submission after validation and (optional) confirmation
  const performUpdate = async (payload) => {
    setSaving(true);
    try {
      await updateEmployee({
        employeeData: payload,
        id: employee?.id,
      }).unwrap();
      refetch();
      setHasChanges(false);
      setShowConfirmModal(false);
      toast.success('Employee Updated Successfully');
    } catch (error) {
      if (error?.data) {
        setErrors({ submit: error?.data?.message });
      } else {
        console.error('Error updating employee:', error);
        setErrors({ submit: 'Error updating employee. Please try again.' });
      }
    } finally {
      setSaving(false);
    }
  };

  // Handle form submission for specific tab
  const handleSubmit = async (e, tab = null) => {
    if (e) {
      e?.preventDefault();
    }

    if (!validateForm(tab)) {
      return;
    }

    const {
      first_name,
      middle_name,
      last_name,
      email,
      qualification,
      mobile,
      role,
      monthlyIncentives,
      override_settings,
      department_id,
      designation_id,
      salary,
      joining_date,
      date_of_birth,
      gender,
      current_address,
      permanent_address,
      emergency_contact_number,
      college_name,
      marital_status,
      city,
      current_experience_in_months,
      previous_experience_in_months,
    } = formData;

    const normalizedIncentives = (monthlyIncentives || []).map((mi) => ({
      title: mi?.title || '',
      amount: mi?.amount !== '' && mi?.amount != null ? Number(mi?.amount) : 0,
      id: mi?.id ? mi.id : null,
    }));

    const payload = {
      current_experience_in_months: Number(current_experience_in_months),
      previous_experience_in_months: Number(previous_experience_in_months),
      joining_date: joining_date ? new Date(joining_date).toISOString() : null,
      department_id: department_id ? Number(department_id) : null,
      designation_id: designation_id ? Number(designation_id) : null,
      first_name,
      middle_name,
      last_name,
      email,
      qualification: qualification || null,
      mobile,
      role,
      date_of_birth: date_of_birth ? new Date(date_of_birth).toISOString() : null,
      gender: gender || null,
      current_address: current_address || null,
      permanent_address: permanent_address || null,
      emergency_contact_number: emergency_contact_number || null,
      college_name: college_name || null,
      marital_status: marital_status || null,
      city: city || null,
      paid_leave: String(formData?.paid_leave || 0),
      salary: {
        ...salary,
        basic_salary: salary.basic_salary ? Number(salary.basic_salary) : 0,
        annual_salary: salary.annual_salary ? Number(salary.annual_salary) : 0,
        monthly_salary: salary.monthly_salary ? Number(salary.monthly_salary) : 0,
        house_rent_allowance: salary.house_rent_allowance ? Number(salary.house_rent_allowance) : 0,
        special_allowance: salary.special_allowance ? Number(salary.special_allowance) : 0,
        other_allowance: salary.other_allowance ? Number(salary.other_allowance) : 0,
      },
      monthlyIncentives: normalizedIncentives,
      override_settings: override_settings ? true : false,
    };

    if (role !== employee.role) {
      setModalConfig({
        title: 'Confirm Role Change',
        message: `Are you sure you want to change the role of this employee from ${employee.role} to ${role}?`,
        onConfirm: () => performUpdate(payload),
      });
      setShowConfirmModal(true);
    } else {
      await performUpdate(payload);
    }
  };

  // Handle save for specific tab
  const handleTabSave = async (tab) => {
    await handleSubmit(null, tab);
  };

  // Handle navigation back with unsaved changes warning
  const handleBack = (target = '/employee-management') => {
    if (hasChanges) {
      setModalConfig({
        title: 'Unsaved Changes',
        message: 'You have unsaved changes. Are you sure you want to leave?',
        onConfirm: () => {
          setShowConfirmModal(false);
          navigate(target);
        },
      });
      setShowConfirmModal(true);
    } else {
      navigate(target);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <Sidebar />
        <CommandPalette />

        <main className="lg:ml-60 pt-16">
          <div className="flex items-center justify-center min-h-96">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
          </div>
        </main>
      </div>
    );
  }

  if (!employeeData?.data) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <Sidebar />
        <CommandPalette />

        <main className="lg:ml-60 pt-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <div className="text-center">
              <Icon
                name="AlertCircle"
                size={48}
                color="var(--color-error)"
                className="mx-auto mb-4"
              />
              <h2 className="text-2xl font-semibold text-gray-900 mb-2">Employee Not Found</h2>
              <p className="text-gray-600 mb-6">The employee you're looking for doesn't exist.</p>
              <button
                onClick={() => navigate('/employee-management')}
                className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-600 transition-colors"
              >
                Back to Employee Management
              </button>
            </div>
          </div>
        </main>
      </div>
    );
  }

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 },
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <Sidebar />
      <CommandPalette />

      <main className="lg:ml-60 pt-16">
        <motion.div
          className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8"
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          {/* Header Section */}
          <motion.div className="mb-8" variants={itemVariants}>
            <Breadcrumb
              items={[
                { label: 'Employees', onClick: () => handleBack('/employee-management') },
                { label: 'Employee Details' },
              ]}
            />
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <button
                  onClick={() => handleBack()}
                  className="flex items-center space-x-2 text-gray-600 hover:text-gray-900 transition-colors"
                >
                  <Icon name="ArrowLeft" size={20} />
                  <span className="text-sm font-medium">Back to Employee Management</span>
                </button>
              </div>
            </div>

            <div className="mt-4">
              <h1 className="text-2xl lg:text-3xl font-semibold text-gray-900">
                Edit Employee: {employee?.name}
              </h1>
              <p className="text-gray-600 mt-1">Update employee information and work details</p>
            </div>
          </motion.div>

          {/* Tabs Navigation */}
          <motion.div
            className="bg-white rounded-lg shadow-sm border border-gray-200 mb-6"
            variants={itemVariants}
          >
            <div className="border-b border-gray-200">
              <nav className="flex space-x-8 px-6">
                <button
                  type="button"
                  onClick={() => handleTabChange('personal')}
                  className={`py-4 px-1 border-b-2 font-medium text-sm outline-none focus:outline-none transition-colors duration-200 ${
                    activeTab === 'personal'
                      ? 'border-primary text-primary'
                      : 'border-transparent text-text-secondary hover:text-text-primary'
                  }`}
                >
                  Information
                </button>
                <button
                  type="button"
                  onClick={() => handleTabChange('family-details')}
                  className={`py-4 px-1 border-b-2 font-medium text-sm outline-none focus:outline-none transition-colors duration-200 ${
                    activeTab === 'family-details'
                      ? 'border-primary text-primary'
                      : 'border-transparent text-text-secondary hover:text-text-primary'
                  }`}
                >
                  Family Details
                </button>
                <button
                  type="button"
                  onClick={() => handleTabChange('bank-details')}
                  className={`py-4 px-1 border-b-2 font-medium text-sm outline-none focus:outline-none transition-colors duration-200 ${
                    activeTab === 'bank-details'
                      ? 'border-primary text-primary'
                      : 'border-transparent text-text-secondary hover:text-text-primary'
                  }`}
                >
                  Bank Details
                </button>
                <button
                  type="button"
                  onClick={() => handleTabChange('bond-details')}
                  className={`py-4 px-1 border-b-2 font-medium text-sm outline-none focus:outline-none transition-colors duration-200 ${
                    activeTab === 'bond-details'
                      ? 'border-primary text-primary'
                      : 'border-transparent text-text-secondary hover:text-text-primary'
                  }`}
                >
                  Bond Details
                </button>
                <button
                  type="button"
                  onClick={() => handleTabChange('permissions')}
                  className={`py-4 px-1 border-b-2 font-medium text-sm outline-none focus:outline-none transition-colors duration-200 ${
                    activeTab === 'permissions'
                      ? 'border-primary text-primary'
                      : 'border-transparent text-text-secondary hover:text-text-primary'
                  }`}
                >
                  Permissions
                </button>
              </nav>
            </div>
          </motion.div>

          {/* Form */}
          <motion.div className="space-y-8" variants={itemVariants}>
            {/* Personal Information Tab */}
            {activeTab === 'personal' && (
              <form onSubmit={(e) => handleSubmit(e)} className="space-y-8">
                <div
                  key="personal"
                  className="bg-white rounded-lg shadow-sm border border-gray-200 animate-fadeIn"
                >
                  <div className="px-6 py-4 border-b border-gray-200">
                    <h2 className="text-lg font-medium text-gray-900 flex items-center space-x-2">
                      <Icon name="User" size={20} color="var(--color-primary)" />
                      <span>Personal Information</span>
                    </h2>
                  </div>

                  <div className="p-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      {/* Name */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          First Name *
                        </label>
                        <input
                          type="text"
                          name="first_name"
                          value={formData?.first_name}
                          onChange={handleInputChange}
                          className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors ${
                            errors?.first_name ? 'border-red-500' : 'border-gray-300'
                          }`}
                          placeholder="Enter first name"
                        />
                        {errors?.first_name && (
                          <p className="mt-1 text-sm text-red-600">{errors?.first_name}</p>
                        )}
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Middle Name *
                        </label>
                        <input
                          type="text"
                          name="middle_name"
                          value={formData?.middle_name}
                          onChange={handleInputChange}
                          className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors ${
                            errors?.middle_name ? 'border-red-500' : 'border-gray-300'
                          }`}
                          placeholder="Enter middle name"
                        />
                        {errors?.middle_name && (
                          <p className="mt-1 text-sm text-red-600">{errors?.middle_name}</p>
                        )}
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Last Name *
                        </label>
                        <input
                          type="text"
                          name="last_name"
                          value={formData?.last_name}
                          onChange={handleInputChange}
                          className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors ${
                            errors?.last_name ? 'border-red-500' : 'border-gray-300'
                          }`}
                          placeholder="Enter full name"
                        />
                        {errors?.last_name && (
                          <p className="mt-1 text-sm text-red-600">{errors?.last_name}</p>
                        )}
                      </div>

                      {/* Email */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Email *
                        </label>
                        <input
                          type="email"
                          name="email"
                          value={formData?.email}
                          onChange={handleInputChange}
                          className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors ${
                            errors?.email ? 'border-red-500' : 'border-gray-300'
                          }`}
                          placeholder="Enter email address"
                        />
                        {errors?.email && (
                          <p className="mt-1 text-sm text-red-600">{errors?.email}</p>
                        )}
                      </div>

                      {/* Qualification */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Qualification
                        </label>
                        <input
                          type="text"
                          name="qualification"
                          value={formData?.qualification}
                          onChange={handleInputChange}
                          className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors ${
                            errors?.qualification ? 'border-red-500' : 'border-gray-300'
                          }`}
                          placeholder="Enter qualification"
                        />
                        {errors?.qualification && (
                          <p className="mt-1 text-sm text-red-600">{errors?.qualification}</p>
                        )}
                      </div>

                      {/* Date of Birth */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Date of Birth
                        </label>
                        <input
                          type="date"
                          name="date_of_birth"
                          value={formData?.date_of_birth}
                          onChange={handleInputChange}
                          className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors ${
                            errors?.date_of_birth ? 'border-red-500' : 'border-gray-300'
                          }`}
                        />
                        {errors?.date_of_birth && (
                          <p className="mt-1 text-sm text-red-600">{errors?.date_of_birth}</p>
                        )}
                      </div>

                      {/* Gender */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Gender
                        </label>
                        <select
                          name="gender"
                          value={formData?.gender}
                          onChange={handleInputChange}
                          className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors ${
                            errors?.gender ? 'border-red-500' : 'border-gray-300'
                          }`}
                        >
                          <option value="">Select Gender</option>
                          <option value="Male">Male</option>
                          <option value="Female">Female</option>
                        </select>
                        {errors?.gender && (
                          <p className="mt-1 text-sm text-red-600">{errors?.gender}</p>
                        )}
                      </div>

                      {/* Martial Status */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Marital Status
                        </label>
                        <select
                          name="marital_status"
                          value={formData?.marital_status}
                          onChange={handleInputChange}
                          className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors ${
                            errors?.marital_status ? 'border-red-500' : 'border-gray-300'
                          }`}
                        >
                          <option value="">Select Status</option>
                          <option value="Single">Single</option>
                          <option value="Married">Married</option>
                        </select>
                        {errors?.marital_status && (
                          <p className="mt-1 text-sm text-red-600">{errors?.marital_status}</p>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Contact Information */}
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 animate-fadeIn">
                  <div className="px-6 py-4 border-b border-gray-200">
                    <h2 className="text-lg font-medium text-gray-900 flex items-center space-x-2">
                      <Icon name="Phone" size={20} color="var(--color-primary)" />
                      <span>Contact Information </span>
                    </h2>
                  </div>

                  <div className="p-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      {/* Mobile Number */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Mobile Number *
                        </label>
                        <input
                          type="tel"
                          name="mobile"
                          value={formData?.mobile}
                          onChange={handleInputChange}
                          className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors ${
                            errors?.mobile ? 'border-red-500' : 'border-gray-300'
                          }`}
                          placeholder="Enter 10-digit mobile number"
                          maxLength="10"
                        />
                        {errors?.mobile && (
                          <p className="mt-1 text-sm text-red-600">{errors?.mobile}</p>
                        )}
                      </div>

                      {/* Emergency Contact */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Emergency Contact Number
                        </label>
                        <input
                          type="tel"
                          name="emergency_contact_number"
                          value={formData?.emergency_contact_number}
                          onChange={handleInputChange}
                          className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors ${
                            errors?.emergency_contact_number ? 'border-red-500' : 'border-gray-300'
                          }`}
                          placeholder="Enter emergency contact"
                        />
                        {errors?.emergency_contact_number && (
                          <p className="mt-1 text-sm text-red-600">
                            {errors?.emergency_contact_number}
                          </p>
                        )}
                      </div>

                      {/* City */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">City</label>
                        <input
                          type="text"
                          name="city"
                          value={formData?.city}
                          onChange={handleInputChange}
                          className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors ${
                            errors?.city ? 'border-red-500' : 'border-gray-300'
                          }`}
                          placeholder="Enter city"
                        />
                        {errors?.city && (
                          <p className="mt-1 text-sm text-red-600">{errors?.city}</p>
                        )}
                      </div>

                      {/* Spacer or empty div to align addresses if needed, but grid will handle it */}
                      <div></div>

                      {/* Current Address */}
                      <div className="md:col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Current Address
                        </label>
                        <textarea
                          name="current_address"
                          value={formData?.current_address}
                          onChange={handleInputChange}
                          rows="2"
                          className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors ${
                            errors?.current_address ? 'border-red-500' : 'border-gray-300'
                          }`}
                          placeholder="Enter current address"
                        ></textarea>
                        {errors?.current_address && (
                          <p className="mt-1 text-sm text-red-600">{errors?.current_address}</p>
                        )}
                      </div>

                      {/* Permanent Address */}
                      <div className="md:col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Permanent Address
                        </label>
                        <textarea
                          name="permanent_address"
                          value={formData?.permanent_address}
                          onChange={handleInputChange}
                          rows="2"
                          className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors ${
                            errors?.permanent_address ? 'border-red-500' : 'border-gray-300'
                          }`}
                          placeholder="Enter permanent address"
                        ></textarea>
                        {errors?.permanent_address && (
                          <p className="mt-1 text-sm text-red-600">{errors?.permanent_address}</p>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
                <div
                  key="work"
                  className="bg-white rounded-lg shadow-sm border border-gray-200 animate-fadeIn"
                >
                  <div className="px-6 py-4 border-b border-gray-200">
                    <h2 className="text-lg font-medium text-gray-900 flex items-center space-x-2">
                      <Icon name="Briefcase" size={20} color="var(--color-primary)" />
                      <span>Work Information</span>
                    </h2>
                  </div>

                  <div className="p-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      {/* Department */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Department
                        </label>
                        <select
                          name="department_id"
                          value={formData?.department_id}
                          onChange={handleInputChange}
                          className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors ${
                            errors?.department_id ? 'border-red-500' : 'border-gray-300'
                          }`}
                        >
                          <option value="">Select Department</option>
                          {departments?.map((dept) => (
                            <option key={dept?.id} value={dept?.id}>
                              {dept?.name}
                            </option>
                          ))}
                        </select>
                        {errors?.department_id && (
                          <p className="mt-1 text-sm text-red-600">{errors?.department_id}</p>
                        )}
                      </div>

                      {/* Designation */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Designation
                        </label>
                        <select
                          name="designation_id"
                          value={formData?.designation_id}
                          onChange={handleInputChange}
                          className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors ${
                            errors?.designation_id ? 'border-red-500' : 'border-gray-300'
                          }`}
                          disabled={!formData?.department_id}
                        >
                          <option value="">
                            {formData?.department_id
                              ? 'Select Designation'
                              : 'Select Department First'}
                          </option>
                          {designationsData?.map((des) => (
                            <option key={des?.id} value={des?.id}>
                              {des?.name}
                            </option>
                          ))}
                        </select>
                        {errors?.designation_id && (
                          <p className="mt-1 text-sm text-red-600">{errors?.designation_id}</p>
                        )}
                      </div>

                      {/* Role */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Role *
                        </label>
                        <select
                          name="role"
                          value={formData?.role}
                          onChange={handleInputChange}
                          className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors ${
                            errors?.role ? 'border-red-500' : 'border-gray-300'
                          }`}
                        >
                          <option value="EMPLOYEE">Employee</option>
                          <option value="ADMIN">Admin</option>
                        </select>
                        {errors?.role && (
                          <p className="mt-1 text-sm text-red-600">{errors?.role}</p>
                        )}
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Joining Date *
                        </label>
                        <input
                          type="date"
                          name="joining_date"
                          value={formData?.joining_date}
                          onChange={handleInputChange}
                          className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors ${
                            errors?.joining_date ? 'border-red-500' : 'border-gray-300'
                          }`}
                        />
                        {errors?.joining_date && (
                          <p className="mt-1 text-sm text-red-600">{errors?.joining_date}</p>
                        )}
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Previous Experience (in Months)
                        </label>
                        <input
                          type="number"
                          name="previous_experience_in_months"
                          value={formData?.previous_experience_in_months}
                          onChange={handleInputChange}
                          className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors ${
                            errors?.previous_experience_in_months
                              ? 'border-red-500'
                              : 'border-gray-300'
                          }`}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Current Experience (in Months)
                        </label>
                        <input
                          type="number"
                          name="current_experience_in_months"
                          value={formData?.current_experience_in_months}
                          onChange={handleInputChange}
                          className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors ${
                            errors?.current_experience_in_months
                              ? 'border-red-500'
                              : 'border-gray-300'
                          }`}
                        />
                      </div>

                      {/* College Name */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          College Name
                        </label>
                        <input
                          type="text"
                          name="college_name"
                          value={formData?.college_name}
                          onChange={handleInputChange}
                          className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors ${
                            errors?.college_name ? 'border-red-500' : 'border-gray-300'
                          }`}
                          placeholder="Enter college name"
                        />
                        {errors?.college_name && (
                          <p className="mt-1 text-sm text-red-600">{errors?.college_name}</p>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Training & Employment Dates */}
                <div id="employee-periods-section">
                  <EmployeePeriods userId={employee?.id} />
                </div>
                <div
                  key="salary-info"
                  id="salary-info"
                  className="bg-white rounded-lg shadow-sm border border-gray-200 animate-fadeIn"
                >
                  <div className="px-6 py-4 border-b border-gray-200">
                    <h2 className="text-lg font-medium text-gray-900 flex items-center space-x-2">
                      <Icon name="Banknote" size={20} color="var(--color-primary)" />
                      <span>Salary Information</span>
                    </h2>
                  </div>
                  <div className="p-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      {/* Annual Salary */}

                      {/* House Rent Allowance */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Basic Salary
                        </label>
                        <div className="relative">
                          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            <span className="text-gray-500 sm:text-sm">₹</span>
                          </div>
                          <input
                            type="number"
                            name="basic_salary"
                            value={formData?.salary?.basic_salary}
                            onChange={handleInputChange}
                            className={`w-full pl-8 pr-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors ${
                              errors?.basic_salary ? 'border-red-500' : 'border-gray-300'
                            }`}
                            placeholder="Enter Basic Salary"
                            min="0"
                            step="0.01"
                          />
                        </div>
                        {errors?.basic_salary && (
                          <p className="mt-1 text-sm text-red-600">{errors?.basic_salary}</p>
                        )}
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          House Rent Allowance (HRA)
                        </label>
                        <div className="relative">
                          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            <span className="text-gray-500 sm:text-sm">₹</span>
                          </div>
                          <input
                            type="number"
                            name="house_rent_allowance"
                            value={formData?.salary?.house_rent_allowance}
                            onChange={handleInputChange}
                            className={`w-full pl-8 pr-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors ${
                              errors?.house_rent_allowance ? 'border-red-500' : 'border-gray-300'
                            }`}
                            placeholder="Enter HRA"
                            min="0"
                            step="0.01"
                          />
                        </div>
                        {errors?.house_rent_allowance && (
                          <p className="mt-1 text-sm text-red-600">
                            {errors?.house_rent_allowance}
                          </p>
                        )}
                      </div>

                      {/* Special Allowance */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Special Allowance (SA)
                        </label>
                        <div className="relative">
                          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            <span className="text-gray-500 sm:text-sm">₹</span>
                          </div>
                          <input
                            type="number"
                            name="special_allowance"
                            value={formData?.salary?.special_allowance}
                            onChange={handleInputChange}
                            className={`w-full pl-8 pr-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors ${
                              errors?.special_allowance ? 'border-red-500' : 'border-gray-300'
                            }`}
                            placeholder="Enter special allowance"
                            min="0"
                            step="0.01"
                          />
                        </div>
                        {errors?.special_allowance && (
                          <p className="mt-1 text-sm text-red-600">{errors?.special_allowance}</p>
                        )}
                      </div>

                      {/* Other Allowance */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Other Allowance (OA)
                        </label>
                        <div className="relative">
                          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            <span className="text-gray-500 sm:text-sm">₹</span>
                          </div>
                          <input
                            type="number"
                            name="other_allowance"
                            value={formData?.salary?.other_allowance}
                            onChange={handleInputChange}
                            className={`w-full pl-8 pr-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors ${
                              errors?.other_allowance ? 'border-red-500' : 'border-gray-300'
                            }`}
                            placeholder="Enter other allowance"
                            min="0"
                            step="0.01"
                          />
                        </div>
                        {errors?.other_allowance && (
                          <p className="mt-1 text-sm text-red-600">{errors?.other_allowance}</p>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="p-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="md:col-span-2">
                        <div className="flex items-center justify-between mb-4">
                          <label className="block text-sm font-medium text-gray-700">
                            Monthly Incentives
                          </label>
                          <button
                            type="button"
                            onClick={addIncentive}
                            className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-primary hover:bg-primary-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                          >
                            <Icon name="Plus" size={16} className="mr-1" />
                            Add Incentive
                          </button>
                        </div>

                        {formData?.monthlyIncentives?.length === 0 ? (
                          <div className="text-center py-8 bg-gray-50 rounded-lg border border-gray-200">
                            <Icon
                              name="Gift"
                              size={32}
                              color="var(--color-gray-400)"
                              className="mx-auto mb-2"
                            />
                            <p className="text-sm text-gray-500">No incentives added yet</p>
                            <p className="text-xs text-gray-400 mt-1">
                              Click "Add Incentive" to get started
                            </p>
                          </div>
                        ) : (
                          <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-gray-200 border border-gray-200 rounded-lg">
                              <thead className="bg-gray-50">
                                <tr>
                                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Title
                                  </th>
                                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Amount
                                  </th>
                                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider w-20">
                                    Actions
                                  </th>
                                </tr>
                              </thead>
                              <tbody className="bg-white divide-y divide-gray-200">
                                {formData?.monthlyIncentives?.map((incentive, index) => (
                                  <tr key={index} className="hover:bg-gray-50">
                                    <td className="px-4 py-3">
                                      <input
                                        type="text"
                                        value={incentive?.title || ''}
                                        onChange={(e) =>
                                          updateIncentive(index, 'title', e.target.value)
                                        }
                                        className={`w-full px-2 py-1 text-sm border rounded focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500 ${
                                          errors?.[`monthlyIncentives_${index}_title`]
                                            ? 'border-red-500'
                                            : 'border-gray-300'
                                        }`}
                                        placeholder="e.g., Performance Bonus"
                                      />
                                      {errors?.[`monthlyIncentives_${index}_title`] && (
                                        <p className="mt-1 text-xs text-red-600">
                                          {errors[`monthlyIncentives_${index}_title`]}
                                        </p>
                                      )}
                                    </td>
                                    <td className="px-4 py-3">
                                      <div className="relative">
                                        <div className="absolute inset-y-0 left-0 pl-2 flex items-center pointer-events-none">
                                          <span className="text-gray-500 text-sm">₹</span>
                                        </div>
                                        <input
                                          type="number"
                                          value={incentive?.amount || ''}
                                          onChange={(e) =>
                                            updateIncentive(index, 'amount', e.target.value)
                                          }
                                          className={`w-full pl-6 pr-2 py-1 text-sm border rounded focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500 ${
                                            errors?.[`monthlyIncentives_${index}_amount`]
                                              ? 'border-red-500'
                                              : 'border-gray-300'
                                          }`}
                                          placeholder="0.00"
                                          min="0"
                                          step="0.01"
                                        />
                                      </div>
                                      {errors?.[`monthlyIncentives_${index}_amount`] && (
                                        <p className="mt-1 text-xs text-red-600">
                                          {errors[`monthlyIncentives_${index}_amount`]}
                                        </p>
                                      )}
                                    </td>
                                    <td className="px-4 py-3 text-center">
                                      <button
                                        type="button"
                                        onClick={() => removeIncentive(index)}
                                        className="text-red-500 hover:text-red-700 p-1"
                                        title="Remove Incentive"
                                      >
                                        <Icon name="Trash" size={16} />
                                      </button>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        )}
                        <p className="mt-2 text-xs text-gray-500">
                          Add monthly incentives like performance bonuses, allowances, etc.
                        </p>

                        <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-6 border-t border-gray-200 pt-6">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Annual Salary
                            </label>
                            <div className="relative">
                              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                <span className="text-gray-500 sm:text-sm">₹</span>
                              </div>
                              <input
                                type="text"
                                name="annual_salary"
                                value={formData?.salary?.annual_salary}
                                className="w-full pl-8 pr-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-500 cursor-not-allowed"
                                placeholder="Calculated annual salary"
                                disabled
                                readOnly
                              />
                            </div>
                            {errors?.annual_salary && (
                              <p className="mt-1 text-sm text-red-600">{errors?.annual_salary}</p>
                            )}
                            <p className="mt-1 text-xs text-gray-500">
                              Automatically calculated (Monthly * 12)
                            </p>
                          </div>

                          {/* Monthly Salary (Calculated) */}
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Monthly Salary (Calculated)
                            </label>
                            <div className="relative">
                              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                <span className="text-gray-500 sm:text-sm">₹</span>
                              </div>
                              <input
                                type="text"
                                name="monthly_salary"
                                id="monthly_salary"
                                value={formData?.salary?.monthly_salary}
                                className="w-full pl-8 pr-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-500 cursor-not-allowed"
                                disabled
                                readOnly
                              />
                            </div>
                            <p className="mt-1 text-xs text-gray-500">
                              Automatically calculated (Basic + HRA + SA + OA + Incentives)
                            </p>
                          </div>
                        </div>
                      </div>
                      <div className="md:col-span-2  py-10">
                        {/* Override Settings Toggle */}
                        <div className="mb-4 pb-4 border-b border-blue-200">
                          <div className="flex items-center justify-between">
                            <div>
                              <label className="block text-sm font-medium text-gray-700">
                                Override Default Settings
                              </label>
                              <p className="text-xs text-gray-500 mt-1">
                                Enable to customize overtime and leave settings for this employee
                              </p>
                            </div>
                            <label className="relative inline-flex items-center cursor-pointer">
                              <input
                                type="checkbox"
                                name="override_settings"
                                checked={formData?.override_settings}
                                onChange={handleInputChange}
                                className="sr-only peer"
                              />
                              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
                              <span className="ml-3 text-sm font-medium text-gray-700">
                                {formData?.override_settings ? 'Custom' : 'Default'}
                              </span>
                            </label>
                          </div>
                        </div>

                        {/* Main Settings Section */}
                        <div
                          className={`flex items-center justify-between ${!formData?.override_settings ? 'opacity-60' : ''}`}
                        >
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Overtime Eligibility
                            </label>
                            <div className="flex items-center space-x-3">
                              <label
                                className={`relative inline-flex items-center ${formData?.override_settings ? 'cursor-pointer' : 'cursor-not-allowed'}`}
                              >
                                <input
                                  type="checkbox"
                                  name="overtime_enabled"
                                  checked={formData?.salary?.overtime_enabled}
                                  onChange={handleInputChange}
                                  disabled={!formData?.override_settings}
                                  className="sr-only peer"
                                />
                                <div
                                  className={`w-11 h-6 bg-gray-200 peer-focus:outline-none ${formData?.override_settings ? 'peer-focus:ring-4 peer-focus:ring-primary-300' : ''} rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary peer-disabled:opacity-50 peer-disabled:cursor-not-allowed`}
                                ></div>
                                <span className="ml-3 text-sm font-medium text-gray-700">
                                  {formData?.overtime_enabled ? 'Enabled' : 'Disabled'}
                                </span>
                              </label>
                            </div>
                            <p className="mt-1 text-xs text-gray-500">
                              Allow employee to work overtime and receive overtime pay
                            </p>
                            {!formData?.override_settings && (
                              <p className="mt-1 text-xs text-amber-600 font-medium">
                                🔒 Using default settings
                              </p>
                            )}
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Paid Leave (Monthly)
                            </label>
                            <div className="flex items-center space-x-3">
                              <div className="relative">
                                <input
                                  type="number"
                                  name="paid_leave"
                                  value={formData?.paid_leave}
                                  onChange={handleInputChange}
                                  disabled={!formData?.override_settings}
                                  className={`px-3 py-2 border rounded-lg transition-colors
              ${
                formData?.override_settings
                  ? 'border-gray-300 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-white'
                  : 'border-gray-200 bg-gray-50 cursor-not-allowed text-gray-500'
              }`}
                                  placeholder="Days"
                                />
                                {!formData?.override_settings && (
                                  <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                                    <svg
                                      className="h-4 w-4 text-gray-400"
                                      fill="none"
                                      stroke="currentColor"
                                      viewBox="0 0 24 24"
                                    >
                                      <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                                      />
                                    </svg>
                                  </div>
                                )}
                              </div>
                            </div>
                            {!formData?.override_settings && (
                              <p className="mt-1 text-xs text-amber-600 font-medium">
                                🔒 Using default settings
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  {/* Save Button for Salary Information */}
                </div>
                <div className="px-6 py-4 border-t border-gray-200 flex justify-end">
                  <button
                    type="button"
                    onClick={() => handleTabSave('salary-info')}
                    disabled={saving}
                    className=" py-4 px-8 text-sm font-medium text-white bg-primary rounded-lg hover:bg-primary-600 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2 transition-colors"
                  >
                    <span>{saving ? <ThreeDotLoading /> : 'Save Changes'}</span>
                  </button>
                </div>
                {/* Read-only Information - Always visible */}
                <div className="bg-gray-50 rounded-lg border border-gray-200">
                  <div className="px-6 py-4 border-b border-gray-200">
                    <h2 className="text-lg font-medium text-gray-700 flex items-center space-x-2">
                      <Icon name="Lock" size={20} />
                      <span>Read-only Information</span>
                    </h2>
                  </div>

                  <div className="p-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-600 mb-2">
                          Employee ID
                        </label>
                        <div className="flex items-center space-x-2 p-2 bg-white border border-gray-200 rounded-lg">
                          <span className="text-sm text-gray-900 font-mono flex-1">
                            {employee?.employee_id.replace(/./g, '*')}
                          </span>
                          <button
                            type="button"
                            onClick={() => copyToClipboard(employee?.employee_id)}
                            className="text-gray-400 hover:text-gray-600 p-1"
                            title="Copy to clipboard"
                          >
                            <Icon name="Copy" size={16} />
                          </button>
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-600 mb-2">
                          Password
                        </label>
                        <div className="flex items-center space-x-2 p-2 bg-white border border-gray-200 rounded-lg">
                          <span className="text-sm text-gray-900 font-mono flex-1">
                            {employee?.password.replace(/./g, '*')}
                          </span>
                          <button
                            type="button"
                            onClick={() => copyToClipboard(employee?.password)}
                            className="text-gray-400 hover:text-gray-600 p-1"
                            title="Copy to clipboard"
                          >
                            <Icon name="Copy" size={16} />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </form>
            )}
            {activeTab === 'family-details' && (
              <FamilyDetails userId={employee?.id} isAdminView={true} />
            )}
            {/* Bank Details Tab */}
            {activeTab === 'bank-details' && <BankDetailsTab user={employee} />}{' '}
            {/* Bank Details Tab */}
            {activeTab === 'bond-details' && employee && <BondDetailsTab user={employee} />}
            {activeTab === 'permissions' && (
              <Permissions userId={employee?.id} userPermissions={employee?.permissions} />
            )}
            {/* Error Message */}
            {errors?.submit && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <div className="flex items-center space-x-2">
                  <Icon name="AlertCircle" size={16} color="var(--color-error)" />
                  <p className="text-sm text-red-600">{errors?.submit}</p>
                </div>
              </div>
            )}
          </motion.div>
        </motion.div>
      </main>

      <ConfirmationModal
        isOpen={showConfirmModal}
        onClose={() => setShowConfirmModal(false)}
        onConfirm={modalConfig.onConfirm}
        title={modalConfig.title}
        message={modalConfig.message}
        isLoading={saving}
      />
    </div>
  );
};

export default EmployeeEditFullPage;
